import { globalSearchApi, globalSearchBaseUrl } from '@/helpers/api'
import { getYoutubeId } from '@/helpers/converter'
import router from '@/router'
import { defineStore } from 'pinia'
export interface ISearchItem {
  id: number
  url: string
  name: string
  category: string
  summary: string
  imageThumbnail: string
  liveDate: string
  isMSCW?: boolean
  youtubeId?: string // not sure if we still use this. Need to check
}

export interface ISiteNavItem {
  term: string
  url: string
}

export interface IVideoItem {
  url: string
  name: string
  summary: string
  imageThumbnail: string
  liveDate: string
  youtubeId?: string
}

export interface ITabItem {
  key: string
  label: string
  count?: number
}

export type NewsFilterSortKey = 'mostRecent' | 'mostRelevant'
export type NewsFilterGameVersionKey = 'all' | 'ms' | 'mscw'

export interface INewsFilterSortItem {
  key: NewsFilterSortKey
  label: string
}
export interface INewsFilterGameVersionItem {
  key: NewsFilterGameVersionKey
  label: string
}
// Config
const stream_summary = true
const newsItemsMaxNumber = 50
const pagesItemsMaxNumber = 30
const videoItemsMaxNumber = 30
/** How many thumbs up/down calls one search may make before the bar stops responding. */
const maxFeedback = 3

/** Cancels in-flight streaming search when a new keyword search starts. */
let globalSearchAbort: AbortController | null = null
let globalSearchRunId = 0

/** Stops reading the response body early so the connection can be torn down (pairs with fetch `signal` abort). */
function cancelResponseBodyReader(reader: ReadableStreamDefaultReader<Uint8Array>) {
  console.log('cancelResponseBodyReader :', reader)
  reader.cancel().catch(() => {})
}

/** Rating a user can give to one search result block. `neutral` retracts a previous vote. */
export type FeedbackRating = 'up' | 'down' | 'neutral'
/** Which block of the search result page a feedback entry refers to. */
export type FeedbackTarget = 'ai_summary' | 'search_results'

/** Per-target feedback state. Each block rates independently and is reset on every new search. */
export interface IFeedbackState {
  /** The last rating sent. `null` = never voted; `'neutral'` = voted, then retracted. */
  rating: FeedbackRating | null
  /** Thumbs calls made for this target. Capped at `maxFeedback`. */
  ratingCount: number
  commentSubmitted: boolean
}

const emptyFeedbackState = (): IFeedbackState => ({ rating: null, ratingCount: 0, commentSubmitted: false })

interface IGlobalSearchState {
  isDropdownSearchActive: boolean
  searchedValue: string
  /** Correlation id sent with `/search`; the feedback endpoint needs the very same value. */
  searchId: string
  /** Feedback state per rateable block, keyed by the `target` the API expects. */
  feedback: Record<FeedbackTarget, IFeedbackState>
  isLoadingSearchResult: boolean
  showAiSummaryResult: boolean
  isLoadingAiSummaryResult: boolean
  searchResultAiSummary: string
  searchResultSiteNav: ISiteNavItem[]
  searchResultNewsRecent: ISearchItem[]
  searchResultNewsRelevance: ISearchItem[]
  searchResultPages: ISearchItem[]
  searchResultVideos: IVideoItem[]
  selectedNewsFilterSort: NewsFilterSortKey
  selectedNewsFilterGameVersion: NewsFilterGameVersionKey
  searchResultError: Error | null
}

interface ISearchApiResponse {
  generating_ai_summary?: boolean
  ai_summary?: string
  site_nav_search_results?: ISiteNavItem[]
  news_search_results_recent?: ISearchItem[]
  news_search_results_relevance?: ISearchItem[]
  pages_search_results?: ISearchItem[]
  videos_search_results?: IVideoItem[]
}

export const useGlobalSearchStore = defineStore('GlobalSearch', {
  state: (): IGlobalSearchState => ({
    isDropdownSearchActive: false,
    searchedValue: '',
    searchId: '',
    feedback: { ai_summary: emptyFeedbackState(), search_results: emptyFeedbackState() },
    isLoadingSearchResult: false,
    isLoadingAiSummaryResult: false,
    showAiSummaryResult: false,
    searchResultAiSummary: '',
    searchResultSiteNav: [],
    searchResultNewsRecent: [],
    searchResultNewsRelevance: [],
    searchResultPages: [],
    searchResultVideos: [],
    selectedNewsFilterSort: 'mostRecent',
    selectedNewsFilterGameVersion: 'all',
    searchResultError: null, // Not used yet.
  }),
  getters: {
    /** True once a target has spent its `maxFeedback` thumbs calls; that bar stops responding. */
    isRatingLocked(): (target: FeedbackTarget) => boolean {
      return (target) => this.feedback[target].ratingCount >= maxFeedback
    },
    newsFiltersSort(): INewsFilterSortItem[] {
      return [
        { key: 'mostRecent', label: 'Most Recent' },
        { key: 'mostRelevant', label: 'Most Relevant' },
      ]
    },
    newsFiltersGameVersions(): INewsFilterGameVersionItem[] {
      return [
        { key: 'all', label: 'All' },
        { key: 'ms', label: 'MapleStory' },
        { key: 'mscw', label: 'Classic World' },
      ]
    },
    sortedSearchResultNews(): ISearchItem[] {
      const {
        selectedNewsFilterGameVersion,
        selectedNewsFilterSort,
        searchResultNewsRecent,
        searchResultNewsRelevance,
      } = this
      let sortedNews: ISearchItem[] = []

      if (selectedNewsFilterSort === 'mostRecent') {
        sortedNews = [...searchResultNewsRecent]
      } else if (selectedNewsFilterSort === 'mostRelevant') {
        sortedNews = [...searchResultNewsRelevance]
      }

      if (selectedNewsFilterGameVersion === 'ms') {
        sortedNews = sortedNews.filter((n) => !n.isMSCW)
      } else if (selectedNewsFilterGameVersion === 'mscw') {
        sortedNews = sortedNews.filter((n) => n.isMSCW)
        console.log(sortedNews, 'sortedNews')
      }
      return sortedNews.slice(0, newsItemsMaxNumber)
    },

    totalResultCount(): number {
      return (this.siteNavCount ?? 0) + (this.newsCount ?? 0) + (this.pagesCount ?? 0) + (this.videosCount ?? 0)
    },
    videosCount(): number {
      return this.searchResultVideos.length
    },
    siteNavCount(): number {
      return this.searchResultSiteNav.length
    },
    newsCount(): number {
      return Math.min(this.searchResultNewsRecent.length, newsItemsMaxNumber)
    },
    pagesCount(): number {
      return this.searchResultPages.length
    },
    tabs(): ITabItem[] {
      return [
        ...(this.showAiSummaryResult && this.totalResultCount > 0 ? [{ key: 'summary', label: 'AI Summary' }] : []),
        ...[
          { key: 'siteNav', label: 'Site Nav', count: this.siteNavCount },
          { key: 'pages', label: 'Pages', count: this.pagesCount },
          { key: 'news', label: 'News', count: this.newsCount },
          { key: 'videos', label: 'Videos', count: this.videosCount },
        ].filter((tab) => tab.count > 0),
      ]
    },
  },
  actions: {
    openGlobalSearchDropDown() {
      this.isDropdownSearchActive = true
    },
    closeGlobalSearchDropDown() {
      this.isDropdownSearchActive = false
    },

    goToGlobalSearchPage(searchValue: string) {
      if (this.isLoadingSearchResult) return
      const _searchValue = searchValue.trim()
      if (_searchValue.length === 0) return // empty value
      router.push({
        name: 'globalSearchIndex',
        query: {
          ...router.currentRoute.value.query,
          searchKeyword: _searchValue,
        },
      })
    },
    searchKeyword(newSearchValue: string) {
      if (this.isLoadingSearchResult) return
      const trimmedNewSearchValue = newSearchValue.trim()
      if (trimmedNewSearchValue.length === 0) return // empty value
      this.searchedValue = trimmedNewSearchValue
      this.searchResultError = null
      this.closeGlobalSearchDropDown()
      this.isLoadingSearchResult = true
      this.resetSearchResult()
      this.startGlobalSearch()
    },

    async startGlobalSearch() {
      globalSearchAbort?.abort()
      const controller = new AbortController()
      globalSearchAbort = controller
      const { signal } = controller
      const requestRunId = ++globalSearchRunId
      // Generate search_id (32 characters). Kept on state so /feedback can reference the same search.
      this.searchId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')

      const payload = {
        search_id: this.searchId,
        search_phrase: this.searchedValue,
        ai_summary: router.currentRoute.value.query.aiSummary === 'false' ? false : true,
        stream_summary,
      }
      try {
        // Same host as `globalSearchApi`, so `/search` and `/feedback` always hit one backend.
        const response = await fetch(`${globalSearchBaseUrl}/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) throw new Error('no body was returned')

        const decoder = new TextDecoder()
        let buffer = ''
        let currentEvent = ''
        let isDone = false

        const stopIfSuperseded = (): boolean => {
          if (signal.aborted || requestRunId !== globalSearchRunId || isDone) {
            cancelResponseBodyReader(reader)
            return true
          }
          return false
        }

        while (true) {
          // Before read: skip work if new request is started.
          if (stopIfSuperseded()) break
          let chunk: ReadableStreamReadResult<Uint8Array>
          try {
            chunk = await reader.read()
          } catch (readErr: unknown) {
            if ((readErr instanceof Error && readErr.name === 'AbortError') || signal.aborted) {
              cancelResponseBodyReader(reader)
              break
            }
            console.error('[GlobalSearch] chunk read error:', {
              name: readErr instanceof Error ? readErr.name : undefined,
              message: readErr instanceof Error ? readErr.message : String(readErr),
              signalAborted: signal.aborted,
              requestRunId,
              globalSearchRunId,
            })
            throw readErr
          }
          const { value, done } = chunk
          if (done) {
            console.log('[GlobalSearch] stream done (read complete)')
            break
          }
          // After await read(): another search may have started while we waited, so check again.
          if (stopIfSuperseded()) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          console.log('Streaming Buffer :', lines)
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (stopIfSuperseded()) break
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim()
            } else if (line.startsWith('data: ')) {
              const dataStr = line.substring(6).trim()
              if (dataStr && currentEvent) {
                try {
                  switch (currentEvent) {
                    case 'results':
                      const data = JSON.parse(dataStr) as ISearchApiResponse
                      console.log('Streaming Result :', data)
                      const {
                        generating_ai_summary,
                        ai_summary,
                        site_nav_search_results,
                        news_search_results_recent,
                        news_search_results_relevance,
                        pages_search_results,
                        videos_search_results,
                      } = data
                      this.searchResultSiteNav = site_nav_search_results ?? []
                      this.searchResultNewsRecent = news_search_results_recent ?? []
                      this.searchResultNewsRelevance = news_search_results_relevance ?? []
                      // this.searchResultPages = pages_search_results.slice(0, pagesItemsMaxNumber) || []
                      // Do not add any page that contains youtubeID in Summary
                      this.searchResultPages = (pages_search_results ?? [])
                        .filter((item) => !item.summary.includes('youtubeId'))
                        .slice(0, pagesItemsMaxNumber)
                      this.searchResultVideos = (videos_search_results ?? [])
                        .slice(0, videoItemsMaxNumber)
                        .map((item) => ({
                          ...item,
                          youtubeId: getYoutubeId(item.url),
                        }))
                      // Show Loading for Ai Summary for later chunk.
                      if (generating_ai_summary) {
                        this.showAiSummaryResult = true
                        this.isLoadingAiSummaryResult = true
                      }
                      // Show Ai Summary if it is available.
                      if (ai_summary) {
                        this.searchResultAiSummary = ai_summary
                        this.showAiSummaryResult = true
                        this.isLoadingAiSummaryResult = false
                      }
                      this.isLoadingSearchResult = false
                      break

                    case 'ai_summary':
                      const aiSummary = JSON.parse(dataStr) as string
                      if (stream_summary) {
                        // Data comes piece by piece
                        this.isLoadingAiSummaryResult = false
                        this.searchResultAiSummary += aiSummary
                      } else {
                        // Data comes all at once
                        this.searchResultAiSummary = aiSummary
                      }
                      break

                    case 'done':
                      isDone = true
                      break

                    default:
                  }
                } catch (e) {
                  console.error('Failed to parse data:', e, dataStr)
                }
              }
            } else if (line.trim() === '') {
              currentEvent = ''
            }
          }
        }
      } catch (error: any) {
        // if the search is aborted or the requestRunId is not the same as the globalSearchRunId,
        // dont show the error
        if (error?.name === 'AbortError' || signal.aborted || requestRunId !== globalSearchRunId) return
        this.searchResultError = error
        console.error('Search error:', error)
      } finally {
        // if the requestRunId is not the same as the globalSearchRunId,
        // dont reset the search result
        if (requestRunId !== globalSearchRunId) return
        this.isLoadingAiSummaryResult = false
        this.isLoadingSearchResult = false
      }
    },
    /**
     * Shared /feedback envelope. Best-effort by design: the UI updates optimistically and every
     * transport/HTTP failure is swallowed. A 200 `{ status: 'duplicate' }` is treated exactly like a 201.
     */
    postFeedback(target: FeedbackTarget, payload: Record<string, unknown>) {
      globalSearchApi
        .post(
          '/feedback',
          {
            // `payload` spreads first so a caller can never silently clobber the envelope keys below.
            ...payload,
            search_id: this.searchId,
            target,
            search_phrase: this.searchedValue,
          },
          // Spec requires credentials: 'include' so the arenaSid / _ga cookies travel with the request.
          // Set per-request rather than on the shared globalSearchApi instance.
          { withCredentials: true },
        )
        .catch(() => {})
    },
    /**
     * Records the thumbs up/down vote for one block.
     * Clicking the already-selected thumb retracts the vote: the UI clears and `neutral` is posted.
     * Switching straight from up to down (or back) is allowed and posts the new rating.
     */
    submitRating(target: FeedbackTarget, rating: Exclude<FeedbackRating, 'neutral'>) {
      if (!this.searchId || this.isRatingLocked(target)) return
      const state = this.feedback[target]
      // State holds exactly what was sent, so `'neutral'` is stored rather than collapsed to `null`.
      // `null` stays reserved for "never voted on this target".
      const nextRating = state.rating === rating ? 'neutral' : rating
      state.rating = nextRating
      state.ratingCount += 1
      this.postFeedback(target, { rating: nextRating })
    },
    /** Sends the free-text comment from one block's feedback popup. */
    submitComment(target: FeedbackTarget, comment: string) {
      const trimmedComment = comment.trim()
      if (!trimmedComment || !this.searchId) return
      this.feedback[target].commentSubmitted = true
      this.postFeedback(target, { feedback_comment: trimmedComment })
    },
    resetSearchResult() {
      this.showAiSummaryResult = false
      this.searchResultAiSummary = ''
      this.searchId = ''
      this.feedback = { ai_summary: emptyFeedbackState(), search_results: emptyFeedbackState() }
      this.searchResultNewsRecent = []
      this.searchResultNewsRelevance = []
      this.searchResultPages = []
      this.searchResultVideos = []
      this.searchResultSiteNav = []
    },
    abortSearch() {
      globalSearchAbort?.abort()
      globalSearchAbort = null
      this.isLoadingSearchResult = false
      this.isLoadingAiSummaryResult = false
    },
    resetSearchedValue() {
      this.searchedValue = ''
    },
    setNewsFilterSort(filterKey: NewsFilterSortKey) {
      this.selectedNewsFilterSort = filterKey
    },
    setNewsFilterGameVersion(filterKey: NewsFilterGameVersionKey) {
      console.log('filterKey :', filterKey)
      this.selectedNewsFilterGameVersion = filterKey
    },
  },
})
